import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useGetBundle } from '~/hooks/apis/accommodation/useBundles';

import BundleContentSection from './BundleContentSection';
import BundleOffersSection from './BundleOffersSection';
import BundlePricingSection from './BundlePricingSection';
import BundleRulesSection from './BundleRulesSection';

interface Props {
  bundleId: string;
  open: boolean;
  onClose: () => void;
}

export default function BundleDetailDialog({ bundleId, open, onClose }: Props) {
  const { data: bundle, isLoading, error } = useGetBundle({ bundleId, enabled: open });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6">{bundle?.name ?? 'Bundle Details'}</Typography>
          {bundle?.status && (
            <Chip
              label={bundle.status}
              color={bundle.status === 'active' ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent>
        {isLoading && (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress variant="indeterminate" />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            <Typography>Failed to load bundle: {error.message}</Typography>
          </Alert>
        )}

        {!isLoading && !error && bundle && (
          <Stack spacing={2}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Rules</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <BundleRulesSection
                  key={`rules-${bundle.rules[0]?.id}-${bundle.rules[0]?.losMin}-${bundle.rules[0]?.leadDaysMin}`}
                  bundle={bundle}
                />
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Pricing</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <BundlePricingSection
                  key={`pricing-${bundle.offers[0]?.id}-${bundle.offers[0]?.price}-${bundle.offers[0]?.currency}`}
                  bundle={bundle}
                />
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Content</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <BundleContentSection key={`content-${bundle.name}`} bundle={bundle} />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Offers ({bundle.offers.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <BundleOffersSection bundleId={bundle.id} offers={bundle.offers} />
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
